import { useEffect, useState, useMemo } from "react";
import AdminLayout from "@/pages/admin/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Pencil, Copy, Archive, Star, StarOff, Upload, Trash2, X, Save,
  ExternalLink, Loader2, Image as ImageIcon, Monitor, Smartphone, FileDown,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Constants — keep in sync with the public detail page + backend schema
// ---------------------------------------------------------------------------
const BEVERAGE_TYPES = [
  "Purified Water", "Alkaline Water", "Spring Water", "Sparkling Water",
  "Coffee", "Tea", "Energy Drink", "Protein Drink", "Functional Beverage",
];
const PRODUCT_CATEGORIES = [
  "Bottled Water", "Coffee", "Tea", "Energy", "Protein", "Supplements", "Functional",
];
const STATUS_OPTIONS = [
  { value: "available", label: "Available" },
  { value: "reserved", label: "Reserved" },
  { value: "sold", label: "Sold" },
  { value: "archived", label: "Archived" },
];

const STATUS_STYLE = {
  available: { bg: "var(--ft-gold)", fg: "var(--ft-black)" },
  reserved: { bg: "#3d3d3d", fg: "#fff" },
  sold: { bg: "#7a1f1f", fg: "#fff" },
  archived: { bg: "#e5e5e5", fg: "#555" },
};

const emptyForm = {
  slug: "",
  brand_name: "",
  tagline: "",
  description: "",
  product_category: "Bottled Water",
  default_beverage_type: "Purified Water",
  available_beverage_options: ["Purified Water", "Alkaline Water", "Spring Water"],
  packaging_type: "500 mL PET Bottle",
  manufacturing_notes: "Manufactured through the FLOWTALIS partner network. Alternative beverage or packaging options subject to availability.",
  category: "",
  positioning: "",
  format: "",
  domain: "Included on transfer",
  hero_image_url: "",
  logo_url: "",
  gallery_images: [],
  desktop_preview_url: "",
  mobile_preview_url: "",
  demo_url: "",
  included_assets: [
    "Brand name & wordmark",
    "Registered domain (transferred to buyer)",
    "Fully designed brand website",
    "Bottle / can label design (production-ready)",
    "Full brand identity system",
    "Manufacturing coordination through FLOWTALIS",
  ],
  status: "available",
  display_order: 100,
  is_featured: false,
  design_edition: "",
  design_edition_label: "",
  exclusive_group: "",
  exclusive_note: "",
};

const slugify = (s) =>
  s.toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 64);

// ---------------------------------------------------------------------------
// AdminTurnkeyBrands — Founder Command Center for the marketplace
// ---------------------------------------------------------------------------
const AdminTurnkeyBrands = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // "new" | slug | null
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get("/admin/turnkey-brands")
      .then((r) => setBrands(r.data.brands || []))
      .catch(() => setBrands([]))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const s = { total: brands.length, available: 0, reserved: 0, sold: 0, archived: 0, featured: 0 };
    brands.forEach((b) => { s[b.status] = (s[b.status] || 0) + 1; if (b.is_featured) s.featured++; });
    return s;
  }, [brands]);

  const openNew = () => { setForm(emptyForm); setEditing("new"); };
  const openEdit = (b) => {
    setForm({
      ...emptyForm,
      ...b,
      available_beverage_options: b.available_beverage_options || [],
      gallery_images: b.gallery_images || [],
      included_assets: b.included_assets || emptyForm.included_assets,
    });
    setEditing(b.slug);
  };
  const close = () => { setEditing(null); setForm(emptyForm); };

  const save = async () => {
    if (!form.brand_name.trim()) return toast.error("Brand name is required.");
    if (!form.slug.trim()) return toast.error("Slug is required.");
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/admin/turnkey-brands", form);
        toast.success(`${form.brand_name} created.`);
      } else {
        await api.put(`/admin/turnkey-brands/${editing}`, form);
        toast.success(`${form.brand_name} updated.`);
      }
      close();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  const duplicate = async (slug) => {
    try {
      const { data } = await api.post(`/admin/turnkey-brands/${slug}/duplicate`);
      toast.success(`Duplicated as ${data.brand.brand_name}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Duplicate failed"); }
  };

  const setStatus = async (slug, status) => {
    try {
      const { data } = await api.patch(`/admin/turnkey-brands/${slug}/status`, { status });
      if (data.cascaded_archived?.length) {
        toast.success(`Status updated. Auto-archived exclusive sibling: ${data.cascaded_archived.join(", ")}`);
      } else {
        toast.success("Status updated.");
      }
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Status update failed"); }
  };

  const toggleFeatured = async (b) => {
    try {
      await api.put(`/admin/turnkey-brands/${b.slug}`, { is_featured: !b.is_featured });
      load();
    } catch (err) { toast.error("Failed to toggle featured"); }
  };

  const archive = async (slug, brandName) => {
    if (!window.confirm(`Archive "${brandName}"? It will be removed from the public marketplace but preserved in the admin.`)) return;
    try {
      await api.delete(`/admin/turnkey-brands/${slug}`);
      toast.success("Brand archived.");
      load();
    } catch (err) { toast.error("Archive failed"); }
  };

  const hardDelete = async (slug, brandName) => {
    if (!window.confirm(`PERMANENTLY delete "${brandName}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/turnkey-brands/${slug}?hard=true`);
      toast.success("Brand permanently deleted.");
      load();
    } catch (err) { toast.error("Delete failed"); }
  };

  const downloadPlaybook = async (slug, brandName) => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/turnkey-brands/${slug}/playbook.pdf`;
      const token = localStorage.getItem("token");
      toast(`Generating playbook for ${brandName}…`);
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("PDF generation failed");
      const blob = await res.blob();
      const dl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dl;
      a.download = `flowtalis-playbook-${slug}.pdf`;
      a.click();
      URL.revokeObjectURL(dl);
      toast.success(`Playbook downloaded — ${brandName}`);
    } catch (err) { toast.error("Playbook generation failed"); }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="ft-eyebrow mb-2">Turnkey Marketplace</div>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="admin-turnkey-title">
            Command Center · Brands
          </h1>
          <p className="text-[var(--ft-muted)] mt-2 max-w-2xl">
            Create, edit, upload assets, and manage the marketplace pipeline. Every change publishes
            instantly — no code deploy required.
          </p>
        </div>
        <button onClick={openNew} className="ft-btn-primary" data-testid="admin-turnkey-new">
          <Plus size={16} /> New Brand
        </button>
      </div>

      {/* STAT STRIP */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8" data-testid="admin-turnkey-stats">
        {[
          { label: "Total", value: stats.total },
          { label: "Available", value: stats.available || 0 },
          { label: "Reserved", value: stats.reserved || 0 },
          { label: "Sold", value: stats.sold || 0 },
          { label: "Featured", value: stats.featured || 0 },
        ].map((k) => (
          <div key={k.label} className="ft-card !p-4">
            <div className="text-[0.6rem] uppercase tracking-[0.22em] text-[var(--ft-muted)] font-semibold">
              {k.label}
            </div>
            <div className="text-2xl font-bold mt-1 tracking-tight text-[var(--ft-black)]">
              {k.value}
            </div>
          </div>
        ))}
      </div>

      {/* TABLE */}
      {loading ? (
        <div className="ft-card fx-skeleton h-64" />
      ) : brands.length === 0 ? (
        <div className="ft-card text-center py-16">
          <ImageIcon size={40} className="mx-auto mb-4 text-[var(--ft-muted)]" />
          <h3 className="text-lg font-semibold">No turnkey brands yet.</h3>
          <p className="text-[var(--ft-muted)] mt-2">Click &ldquo;New Brand&rdquo; to launch your first turnkey listing.</p>
        </div>
      ) : (
        <div className="ft-card !p-0 overflow-x-auto" data-testid="admin-turnkey-table">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-[var(--ft-light-gray)] text-[0.7rem] uppercase tracking-[0.16em] font-semibold text-[var(--ft-charcoal)]">
              <tr>
                <th className="text-left p-4 w-12"></th>
                <th className="text-left p-4">Brand</th>
                <th className="text-left p-4">Beverage</th>
                <th className="text-left p-4">Edition</th>
                <th className="text-left p-4">Status</th>
                <th className="text-left p-4 w-20">Order</th>
                <th className="text-left p-4 w-16">Feat.</th>
                <th className="text-right p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {brands.map((b) => (
                <tr key={b.slug} className="border-t border-[var(--ft-border)] hover:bg-[var(--ft-light-gray)] transition-colors" data-testid={`admin-turnkey-row-${b.slug}`}>
                  <td className="p-4">
                    <div className="h-10 w-10 rounded-lg overflow-hidden bg-[var(--ft-black)] flex items-center justify-center">
                      {b.hero_image_url ? (
                        <img src={b.hero_image_url} alt={b.brand_name} className="w-full h-full object-contain p-1" />
                      ) : (
                        <ImageIcon size={16} color="#666" />
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="font-semibold text-[var(--ft-black)]">{b.brand_name}</div>
                    <div className="text-xs text-[var(--ft-muted)] mt-0.5">{b.slug}</div>
                  </td>
                  <td className="p-4">{b.default_beverage_type || b.category || "—"}</td>
                  <td className="p-4 text-xs">{b.design_edition_label || "—"}</td>
                  <td className="p-4">
                    <select
                      value={b.status}
                      onChange={(e) => setStatus(b.slug, e.target.value)}
                      className="text-xs font-semibold px-2 py-1 rounded-full border-0 cursor-pointer"
                      style={{
                        background: STATUS_STYLE[b.status]?.bg,
                        color: STATUS_STYLE[b.status]?.fg,
                      }}
                      data-testid={`admin-turnkey-status-${b.slug}`}
                    >
                      {STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="p-4 text-[var(--ft-muted)]">{b.display_order ?? "—"}</td>
                  <td className="p-4">
                    <button onClick={() => toggleFeatured(b)} data-testid={`admin-turnkey-feature-${b.slug}`} className="hover:scale-110 transition-transform">
                      {b.is_featured
                        ? <Star size={18} strokeWidth={1.75} fill="var(--ft-gold)" color="var(--ft-gold)" />
                        : <StarOff size={18} strokeWidth={1.75} color="#bbb" />}
                    </button>
                  </td>
                  <td className="p-4 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => openEdit(b)} title="Edit" data-testid={`admin-turnkey-edit-${b.slug}`}
                              className="h-8 w-8 rounded-lg hover:bg-[var(--ft-gold-soft)] flex items-center justify-center transition-colors">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => duplicate(b.slug)} title="Duplicate" data-testid={`admin-turnkey-duplicate-${b.slug}`}
                              className="h-8 w-8 rounded-lg hover:bg-[var(--ft-gold-soft)] flex items-center justify-center transition-colors">
                        <Copy size={14} />
                      </button>
                      <a href={`/turnkey/${b.slug}`} target="_blank" rel="noopener noreferrer" title="View public page"
                         className="h-8 w-8 rounded-lg hover:bg-[var(--ft-gold-soft)] flex items-center justify-center transition-colors">
                        <ExternalLink size={14} />
                      </a>
                      <button onClick={() => downloadPlaybook(b.slug, b.brand_name)} title="Download Sales Playbook PDF" data-testid={`admin-turnkey-playbook-${b.slug}`}
                              className="h-8 w-8 rounded-lg hover:bg-[var(--ft-gold-soft)] flex items-center justify-center transition-colors">
                        <FileDown size={14} />
                      </button>
                      {b.status !== "archived" ? (
                        <button onClick={() => archive(b.slug, b.brand_name)} title="Archive" data-testid={`admin-turnkey-archive-${b.slug}`}
                                className="h-8 w-8 rounded-lg hover:bg-[var(--ft-gold-soft)] flex items-center justify-center transition-colors">
                          <Archive size={14} />
                        </button>
                      ) : (
                        <button onClick={() => hardDelete(b.slug, b.brand_name)} title="Delete permanently" data-testid={`admin-turnkey-delete-${b.slug}`}
                                className="h-8 w-8 rounded-lg hover:bg-red-100 flex items-center justify-center transition-colors">
                          <Trash2 size={14} color="#c53030" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* EDITOR MODAL */}
      {editing && (
        <BrandEditor
          form={form}
          setForm={setForm}
          saving={saving}
          isNew={editing === "new"}
          onClose={close}
          onSave={save}
        />
      )}
    </AdminLayout>
  );
};

// ---------------------------------------------------------------------------
// BrandEditor — full-page modal for creating / editing a turnkey brand
// ---------------------------------------------------------------------------
const BrandEditor = ({ form, setForm, saving, isNew, onClose, onSave }) => {
  const [uploadingKey, setUploadingKey] = useState(null); // hero_image_url | logo_url | ...
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setText = (k) => (e) => set(k)(e.target.value);
  const setNum = (k) => (e) => set(k)(e.target.value === "" ? "" : Number(e.target.value));

  // Auto-slugify brand_name on create if slug is empty
  const onBrandNameChange = (e) => {
    const v = e.target.value;
    setForm((f) => ({
      ...f,
      brand_name: v,
      slug: isNew && !f._slugTouched ? slugify(v) : f.slug,
    }));
  };
  const onSlugChange = (e) => setForm((f) => ({ ...f, slug: slugify(e.target.value), _slugTouched: true }));

  const uploadImage = (key) => async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("Image exceeds 8 MB");
    setUploadingKey(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/uploads/image?purpose=turnkey-${key}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set(key)(data.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingKey(null);
    }
  };

  const uploadGalleryImage = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("Image exceeds 8 MB");
    setUploadingKey("gallery");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads/image?purpose=turnkey-gallery", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("gallery_images")([...(form.gallery_images || []), data.url]);
      toast.success("Added to gallery");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploadingKey(null);
    }
  };

  const removeGalleryImage = (idx) => {
    set("gallery_images")((form.gallery_images || []).filter((_, i) => i !== idx));
  };

  const toggleBeverage = (bev) => {
    const has = (form.available_beverage_options || []).includes(bev);
    set("available_beverage_options")(
      has ? form.available_beverage_options.filter((b) => b !== bev) : [...(form.available_beverage_options || []), bev]
    );
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-4xl w-full my-8 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="admin-turnkey-editor">
        <div className="p-6 border-b border-[var(--ft-border)] flex items-center justify-between sticky top-0 bg-white z-10 rounded-t-2xl">
          <div>
            <div className="ft-eyebrow">Turnkey Brand</div>
            <h2 className="text-xl font-bold tracking-tight mt-1">
              {isNew ? "Create New Brand" : `Edit · ${form.brand_name || form.slug}`}
            </h2>
          </div>
          <button onClick={onClose} className="h-10 w-10 rounded-lg hover:bg-[var(--ft-light-gray)] flex items-center justify-center">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-8">
          {/* IDENTITY */}
          <Section title="Identity">
            <Grid>
              <Field label="Brand Name *">
                <input className="ft-input" value={form.brand_name} onChange={onBrandNameChange} data-testid="admin-turnkey-input-name" />
              </Field>
              <Field label="Slug (URL) *">
                <input className="ft-input font-mono text-sm" value={form.slug} onChange={onSlugChange} disabled={!isNew} data-testid="admin-turnkey-input-slug" />
                {!isNew && <Hint>Slug cannot be changed after creation.</Hint>}
              </Field>
            </Grid>
            <Field label="Tagline">
              <input className="ft-input" value={form.tagline} onChange={setText("tagline")} placeholder="One-line pitch shown on marketplace cards." data-testid="admin-turnkey-input-tagline" />
            </Field>
            <Field label="Description">
              <textarea className="ft-input min-h-[110px]" value={form.description} onChange={setText("description")} data-testid="admin-turnkey-input-description" />
            </Field>
          </Section>

          {/* PRODUCT */}
          <Section title="Product">
            <Grid>
              <Field label="Product Category">
                <select className="ft-input" value={form.product_category} onChange={setText("product_category")}>
                  {PRODUCT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Default Beverage Type">
                <select className="ft-input" value={form.default_beverage_type} onChange={setText("default_beverage_type")}>
                  <option value="">— Select —</option>
                  {BEVERAGE_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Packaging Type">
                <input className="ft-input" value={form.packaging_type} onChange={setText("packaging_type")} placeholder="e.g. 500 mL PET Bottle" />
              </Field>
              <Field label="Format (display copy)">
                <input className="ft-input" value={form.format} onChange={setText("format")} />
              </Field>
            </Grid>
            <Field label="Available Beverage Options (customer flexibility)">
              <div className="flex flex-wrap gap-2 mt-1">
                {BEVERAGE_TYPES.map((bev) => {
                  const active = (form.available_beverage_options || []).includes(bev);
                  return (
                    <button
                      key={bev}
                      type="button"
                      onClick={() => toggleBeverage(bev)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                        active
                          ? "border-[var(--ft-gold)] bg-[var(--ft-gold-soft)] text-[var(--ft-black)]"
                          : "border-[var(--ft-border)] text-[var(--ft-muted)] hover:border-[var(--ft-gold)]"
                      }`}
                      data-testid={`admin-turnkey-bev-${bev.replace(/\s/g, "-").toLowerCase()}`}
                    >
                      {bev}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="Manufacturing Notes">
              <textarea className="ft-input min-h-[70px]" value={form.manufacturing_notes} onChange={setText("manufacturing_notes")} />
            </Field>
          </Section>

          {/* IMAGES */}
          <Section title="Brand Assets">
            <Grid>
              <UploadField
                label="Hero Image *"
                value={form.hero_image_url}
                onUpload={uploadImage("hero_image_url")}
                onClear={() => set("hero_image_url")("")}
                uploading={uploadingKey === "hero_image_url"}
                testId="admin-turnkey-hero"
              />
              <UploadField
                label="Logo (optional)"
                value={form.logo_url}
                onUpload={uploadImage("logo_url")}
                onClear={() => set("logo_url")("")}
                uploading={uploadingKey === "logo_url"}
                testId="admin-turnkey-logo"
              />
            </Grid>

            <Field label="Bottle Mockup / Lifestyle Gallery">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
                {(form.gallery_images || []).map((url, i) => (
                  <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-[var(--ft-black)] group" data-testid={`admin-turnkey-gallery-${i}`}>
                    <img src={url} alt="" className="w-full h-full object-contain p-2" />
                    <button
                      onClick={() => removeGalleryImage(i)}
                      className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      type="button"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
                <label className="aspect-square rounded-lg border-2 border-dashed border-[var(--ft-border)] hover:border-[var(--ft-gold)] flex flex-col items-center justify-center cursor-pointer transition-colors">
                  {uploadingKey === "gallery" ? (
                    <Loader2 size={20} className="animate-spin" />
                  ) : (
                    <>
                      <Upload size={20} className="text-[var(--ft-muted)] mb-1" />
                      <span className="text-[0.65rem] uppercase tracking-[0.14em] font-semibold text-[var(--ft-muted)]">Add Image</span>
                    </>
                  )}
                  <input type="file" accept="image/*" hidden onChange={(e) => uploadGalleryImage(e.target.files?.[0])} data-testid="admin-turnkey-gallery-upload" />
                </label>
              </div>
            </Field>
          </Section>

          {/* WEBSITE PREVIEW */}
          <Section title="Website Preview">
            <Grid>
              <UploadField
                label="Desktop Preview Screenshot"
                value={form.desktop_preview_url}
                onUpload={uploadImage("desktop_preview_url")}
                onClear={() => set("desktop_preview_url")("")}
                uploading={uploadingKey === "desktop_preview_url"}
                icon={Monitor}
                testId="admin-turnkey-desktop"
              />
              <UploadField
                label="Mobile Preview Screenshot"
                value={form.mobile_preview_url}
                onUpload={uploadImage("mobile_preview_url")}
                onClear={() => set("mobile_preview_url")("")}
                uploading={uploadingKey === "mobile_preview_url"}
                icon={Smartphone}
                testId="admin-turnkey-mobile"
              />
            </Grid>
            <Field label="Live Demo URL (optional)">
              <input className="ft-input" value={form.demo_url} onChange={setText("demo_url")} placeholder="https://demo.example.com" data-testid="admin-turnkey-demo-url" />
            </Field>
          </Section>

          {/* MARKETPLACE */}
          <Section title="Marketplace Placement">
            <Grid>
              <Field label="Status">
                <select className="ft-input" value={form.status} onChange={setText("status")} data-testid="admin-turnkey-input-status">
                  {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Field>
              <Field label="Display Order (lower shows first)">
                <input type="number" className="ft-input" value={form.display_order ?? ""} onChange={setNum("display_order")} />
              </Field>
              <Field label="Positioning">
                <input className="ft-input" value={form.positioning} onChange={setText("positioning")} />
              </Field>
              <Field label="Domain Status">
                <input className="ft-input" value={form.domain} onChange={setText("domain")} />
              </Field>
            </Grid>
            <label className="flex items-center gap-3 pt-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.is_featured}
                onChange={(e) => set("is_featured")(e.target.checked)}
                className="w-4 h-4 accent-[var(--ft-gold)]"
                data-testid="admin-turnkey-featured-checkbox"
              />
              <span className="text-sm font-medium">Featured Brand — pins to the top of the homepage rail</span>
            </label>
          </Section>

          {/* EDITIONS / EXCLUSIVITY */}
          <Section title="Design Editions & Exclusivity (optional)">
            <p className="text-xs text-[var(--ft-muted)] mb-3 leading-relaxed">
              Fill these in if this brand is one of several exclusive design editions sharing a single
              trademark. Selling one edition auto-archives the others in the same group.
            </p>
            <Grid>
              <Field label="Design Edition Code">
                <input className="ft-input" value={form.design_edition} onChange={setText("design_edition")} placeholder="A" />
              </Field>
              <Field label="Design Edition Label (public)">
                <input className="ft-input" value={form.design_edition_label} onChange={setText("design_edition_label")} placeholder="Design Edition A · Blue & Gold" />
              </Field>
              <Field label="Exclusive Group (slug)">
                <input className="ft-input font-mono text-sm" value={form.exclusive_group || ""} onChange={setText("exclusive_group")} placeholder="zidwater" />
              </Field>
            </Grid>
            <Field label="Exclusive Note (public copy)">
              <textarea className="ft-input min-h-[70px]" value={form.exclusive_note || ""} onChange={setText("exclusive_note")} />
            </Field>
          </Section>
        </div>

        <div className="p-6 border-t border-[var(--ft-border)] flex items-center justify-end gap-3 sticky bottom-0 bg-white rounded-b-2xl">
          <button onClick={onClose} className="ft-btn-ghost" type="button">Cancel</button>
          <button onClick={onSave} disabled={saving} className="ft-btn-primary" data-testid="admin-turnkey-save">
            {saving ? <><Loader2 size={16} className="animate-spin" /> Saving…</> : <><Save size={16} /> {isNew ? "Create Brand" : "Save Changes"}</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Small local UI primitives — kept here to avoid adding to global components
// ---------------------------------------------------------------------------
const Section = ({ title, children }) => (
  <div>
    <h3 className="text-[0.7rem] uppercase tracking-[0.24em] font-bold text-[var(--ft-gold)] mb-4">{title}</h3>
    <span className="ft-gold-rule block mb-5" />
    <div className="space-y-4">{children}</div>
  </div>
);

const Grid = ({ children }) => <div className="grid md:grid-cols-2 gap-4">{children}</div>;

const Field = ({ label, children }) => (
  <div>
    <label className="ft-label block mb-1.5">{label}</label>
    {children}
  </div>
);

const Hint = ({ children }) => (
  <p className="text-[0.7rem] text-[var(--ft-muted)] mt-1">{children}</p>
);

const UploadField = ({ label, value, onUpload, onClear, uploading, icon: Icon = ImageIcon, testId }) => (
  <Field label={label}>
    <div className="ft-input !p-0 overflow-hidden flex items-stretch">
      <div className="h-14 w-14 shrink-0 bg-[var(--ft-black)] flex items-center justify-center overflow-hidden">
        {value ? (
          <img src={value} alt="" className="w-full h-full object-contain p-1" />
        ) : (
          <Icon size={18} color="#666" />
        )}
      </div>
      <div className="flex-1 flex items-center px-3 min-w-0">
        <span className="text-xs text-[var(--ft-muted)] truncate">
          {value ? value.split("/").pop() : "No image uploaded"}
        </span>
      </div>
      <label className="h-14 px-4 border-l border-[var(--ft-border)] flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] cursor-pointer hover:bg-[var(--ft-gold-soft)] transition-colors shrink-0">
        {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
        Upload
        <input type="file" accept="image/*" hidden onChange={(e) => onUpload(e.target.files?.[0])} data-testid={`${testId}-upload`} />
      </label>
      {value && (
        <button type="button" onClick={onClear} className="h-14 w-12 border-l border-[var(--ft-border)] flex items-center justify-center hover:bg-red-50 shrink-0" title="Clear">
          <X size={14} color="#c53030" />
        </button>
      )}
    </div>
  </Field>
);

export default AdminTurnkeyBrands;
